import React from 'react';
import { Paper, Grid, Box, Typography, Avatar, Chip, Stack } from '@mui/material';
import { formatCurrency } from 'utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ScoreBreakdownItem } from '@trnsact/trnsact-scorecard-sdk';

interface ScoreSummarySectionProps {
  totalScore: number;
  band: string;
  bandColor: string;
  breakDown: ScoreBreakdownItem[];
  creditLimit?: number;
}

export const ScoreSummarySection: React.FC<ScoreSummarySectionProps> = ({
  totalScore,
  band,
  bandColor,
  breakDown,
  creditLimit,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
        mx: 2,
        mt: 1,
        mb: 3,
        p: 4,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              textAlign: 'center',
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              boxShadow: '0 8px 24px rgba(25, 118, 210, 0.25)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 32px rgba(25, 118, 210, 0.35)',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
                pointerEvents: 'none',
              },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                mb: 2.5,
                display: 'block',
                fontWeight: 700,
                letterSpacing: 2,
                color: 'rgba(255, 255, 255, 0.9)',
                textTransform: 'uppercase',
              }}
            >
              Total Score
            </Typography>
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: 3,
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                  border: '4px solid rgba(255, 255, 255, 0.3)',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: -2,
                    borderRadius: 3,
                    padding: 2,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.5), transparent)',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '3rem',
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    lineHeight: 1,
                  }}
                >
                  {totalScore}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    fontSize: '0.7rem',
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  points
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              textAlign: 'center',
              p: 3,
              borderRadius: 2,
              bgcolor: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              },
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{
                mb: 2,
                display: 'block',
                fontWeight: 600,
                letterSpacing: 1.2,
              }}
            >
              Credit Band
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Chip
                label={band || 'Review'}
                sx={{
                  background: band
                    ? `linear-gradient(135deg, ${bandColor} 0%, ${bandColor}dd 100%)`
                    : 'linear-gradient(135deg, #616161 0%, #757575 100%)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1.125rem',
                  py: 3,
                  px: 3,
                  height: 'auto',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                }}
              />
            </Box>
          </Box>
        </Grid>
        {creditLimit != null && creditLimit > 0 && (
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                textAlign: 'center',
                p: 3,
                borderRadius: 2,
                bgcolor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                },
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ mb: 2, display: 'block', fontWeight: 600, letterSpacing: 1.2 }}
              >
                Credit Limit
              </Typography>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'text.primary' }}>
                {formatCurrency(creditLimit, true)}
              </Typography>
            </Box>
          </Grid>
        )}
        {breakDown && breakDown.length > 0 && (
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                },
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{
                  mb: 2,
                  display: 'block',
                  fontWeight: 600,
                  letterSpacing: 1.2,
                }}
              >
                Score Breakdown
              </Typography>
              <Stack spacing={1}>
                {Object.values(
                  breakDown.reduce(
                    (acc, item) => {
                      acc[item.category] = item;
                      return acc;
                    },
                    {} as Record<string, ScoreBreakdownItem>
                  )
                ).map(item => (
                  <Box
                    key={item.category}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1,
                      px: 1.5,
                      borderRadius: 1.5,
                      bgcolor: 'grey.50',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'primary.50',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon
                        sx={{
                          fontSize: '1.125rem',
                          color: 'success.main',
                          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: 'text.primary',
                        }}
                      >
                        {item.category
                          .replace(/_/g, ' ')
                          .toLowerCase()
                          .replace(/\b\w/g, (l: string) => l.toUpperCase())}
                      </Typography>
                    </Box>
                    <Chip
                      label={`+${item.score} pts`}
                      size="small"
                      sx={{
                        minWidth: 55,
                        fontWeight: 700,
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontSize: '0.813rem',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};
